import { type FormEvent, type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, Menu, Plus, Trash2, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import Confirmation from "../../shared/Confirmation";
import { useTranslation } from "../../../i18n";
import { useAdminCopilot } from "../../../hooks/useAdminCopilot";
import AiMark, { AiGlyph } from "../../website/chat/AiMark";
import ChatMarkdown from "../../website/chat/messages/ChatMarkdown";

function inputDirection(value: string): "rtl" | "ltr" | undefined {
  const first = value.trim()[0];
  if (!first) return undefined;
  return /[\u0600-\u06FF]/.test(first) ? "rtl" : "ltr";
}

export default function AdminCopilotPanel({ tenantId }: { tenantId: string }) {
  const { t, language, dir } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [input, setInput] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const locale = language === "ar" ? "ar" : "en";
  const copilot = useAdminCopilot({ tenantId, locale, enabled: Boolean(tenantId) });

  const closePanel = useCallback(() => {
    copilot.resetActiveChat();
    setInput("");
    setHistoryOpen(false);
    setOpen(false);
  }, [copilot]);

  const openPanel = useCallback(() => {
    copilot.resetActiveChat();
    setInput("");
    setHistoryOpen(false);
    setOpen(true);
    void copilot.refreshHistory();
  }, [copilot]);

  const startNewChat = useCallback(() => {
    copilot.resetActiveChat();
    setInput("");
    setHistoryOpen(false);
  }, [copilot]);

  function submit() {
    const message = input.trim();
    if (!message || copilot.readOnly) return;
    setInput("");
    void copilot.send(message, location.pathname);
  }

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pendingDeleteId) closePanel();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [closePanel, open, pendingDeleteId]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTo({ top: list.scrollHeight });
  }, [copilot.messages, copilot.pending]);

  const errorText = copilot.errorCode
    ? t(`admin.copilot.error.${copilot.errorCode}`) === `admin.copilot.error.${copilot.errorCode}`
      ? t("admin.copilot.error")
      : t(`admin.copilot.error.${copilot.errorCode}`)
    : null;

  function renderHistoryList() {
    if (copilot.historyLoading && !copilot.history.length) {
      return (
        <p className="px-4 py-6 text-sm text-muted-foreground">{t("common.loading")}</p>
      );
    }
    if (!copilot.history.length) {
      return (
        <p className="px-4 py-6 text-sm leading-relaxed text-muted-foreground">
          {t("admin.copilot.emptyHistory")}
        </p>
      );
    }
    return (
      <ul className="flex flex-col gap-1 p-2" aria-label={t("admin.copilot.history")}>
        {copilot.history.map((item) => {
          const active = item.id === copilot.conversationId;
          return (
            <li key={item.id}>
              <div
                className={`group flex items-center gap-1 rounded-xl px-1 ${
                  active ? "bg-brand-primary/10" : "hover:bg-muted/70"
                }`}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate rounded-xl px-3 py-2.5 text-start text-sm text-foreground"
                  onClick={() => {
                    setInput("");
                    void copilot.openConversation(item.id);
                    setHistoryOpen(false);
                  }}
                >
                  {item.title || t("admin.copilot.untitled")}
                </button>
                <button
                  type="button"
                  className="shrink-0 rounded-lg p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label={t("admin.copilot.deleteChat")}
                  onClick={() => setPendingDeleteId(item.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <>
      {open ? null : (
        <button
          type="button"
          className="fixed end-4 bottom-4 z-90 flex h-14 w-14 items-center justify-center rounded-full text-[color:var(--ai-on-accent)] shadow-lg"
          style={{ backgroundColor: "var(--ai-accent)" }}
          aria-label={t("admin.copilot.open")}
          onClick={openPanel}
        >
          <AiGlyph className="h-7 w-7" />
        </button>
      )}

      {open ? (
        <div
          className="agent-chat-panel fixed start-0 end-0 bottom-0 z-100 flex flex-col overflow-hidden overscroll-contain bg-background text-foreground lg:start-[248px]"
          style={{ top: "60px" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-copilot-title"
          dir={dir}
        >
          <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2.5 sm:px-4">
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted md:hidden"
              aria-label={t("admin.copilot.history")}
              aria-expanded={historyOpen}
              onClick={() => setHistoryOpen((current) => !current)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <AiMark className="hidden h-9 w-9 sm:inline-flex" />
            <div className="min-w-0 flex-1">
              <h2 id="admin-copilot-title" className="truncate text-base font-semibold">
                {t("admin.copilot.title")}
              </h2>
              <p className="hidden truncate text-xs text-muted-foreground sm:block">
                {copilot.readOnly
                  ? t("admin.copilot.historyReadOnlyTitle")
                  : t("admin.copilot.subtitle")}
              </p>
            </div>
            <button
              type="button"
              className="inline-flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-medium hover:bg-muted"
              onClick={startNewChat}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">{t("admin.copilot.newChat")}</span>
            </button>
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted"
              aria-label={t("admin.copilot.close")}
              onClick={closePanel}
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="relative flex min-h-0 flex-1">
            <aside className="hidden min-h-0 w-72 shrink-0 flex-col border-border bg-muted/30 ltr:order-first ltr:border-e rtl:order-last rtl:border-s md:flex">
              <div className="border-b border-border px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("admin.copilot.history")}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {t("admin.copilot.historyReadOnlyHint")}
                </p>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">{renderHistoryList()}</div>
            </aside>

            {historyOpen ? (
              <div className="absolute inset-0 z-10 flex md:hidden" dir="ltr">
                <div
                  className="flex h-full w-[min(20rem,86vw)] flex-col border-e border-border bg-background shadow-xl"
                  dir={dir}
                >
                  <div className="border-b border-border px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("admin.copilot.history")}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {t("admin.copilot.historyReadOnlyHint")}
                    </p>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto">{renderHistoryList()}</div>
                </div>
                <button
                  type="button"
                  className="min-w-0 flex-1 bg-black/30"
                  aria-label={t("admin.copilot.close")}
                  onClick={() => setHistoryOpen(false)}
                />
              </div>
            ) : null}

            <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-background ltr:order-last rtl:order-first">
              {copilot.messages.length === 0 ? (
                copilot.pending ? (
                  <div className="flex min-h-0 flex-1 items-center justify-center px-5">
                    <ThinkingIndicator label={t("admin.copilot.thinking")} />
                  </div>
                ) : (
                  <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col justify-center px-5 py-8 sm:px-6">
                    <AiMark className="mb-4 h-12 w-12" />
                    <p className="max-w-xl text-lg font-semibold text-foreground sm:text-xl">
                      {t("admin.copilot.emptyTitle")}
                    </p>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {t("admin.copilot.empty")}
                    </p>
                  </div>
                )
              ) : (
                <div
                  ref={listRef}
                  className="mx-auto min-h-0 w-full max-w-3xl flex-1 space-y-5 overflow-x-hidden overflow-y-auto overscroll-contain px-5 py-5 text-sm sm:px-6 sm:text-[0.95rem]"
                >
                  {copilot.messages.map((message) => {
                    const isUser = message.role === "user";
                    return (
                      <article
                        key={message.id}
                        className={
                          isUser
                            ? "relative isolate ms-10 overflow-hidden rounded-2xl rounded-es-md bg-brand-primary/12 p-4 text-start sm:ms-16"
                            : "relative isolate me-6 overflow-hidden rounded-2xl rounded-ee-md border border-border bg-background p-4 text-start shadow-sm sm:me-12 sm:p-5"
                        }
                        dir="auto"
                      >
                        {!isUser ? (
                          <div className="mb-3 flex items-center gap-2">
                            <AiMark className="h-7 w-7" />
                          </div>
                        ) : null}
                        {isUser ? message.text : <ChatMarkdown text={message.text} />}
                      </article>
                    );
                  })}
                  {copilot.pending ? (
                    <div className="pb-1">
                      <ThinkingIndicator label={t("admin.copilot.thinking")} />
                    </div>
                  ) : null}
                </div>
              )}
              {errorText ? (
                <p
                  className="mx-auto w-full max-w-3xl px-5 text-sm text-destructive sm:px-6"
                  role="alert"
                >
                  {errorText}
                </p>
              ) : null}
              {copilot.readOnly ? (
                <div
                  className="border-t border-border bg-muted/40 px-4 py-4 sm:px-6"
                  role="status"
                >
                  <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">
                        {t("admin.copilot.historyReadOnlyTitle")}
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        {t("admin.copilot.historyReadOnly")}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full px-4 text-sm font-medium text-[color:var(--ai-on-accent)]"
                      style={{ backgroundColor: "var(--ai-accent)" }}
                      onClick={startNewChat}
                    >
                      <Plus className="h-4 w-4" />
                      {t("admin.copilot.newChat")}
                    </button>
                  </div>
                </div>
              ) : (
                <Composer
                  value={input}
                  pending={copilot.pending}
                  placeholder={t("admin.copilot.placeholder")}
                  sendLabel={t("admin.copilot.send")}
                  onChange={setInput}
                  onSubmit={submit}
                />
              )}
            </section>
          </div>
        </div>
      ) : null}

      <Confirmation
        open={Boolean(pendingDeleteId)}
        title={t("admin.copilot.deleteChat")}
        message={t("admin.copilot.deleteChatConfirm")}
        confirmLabel={t("common.delete")}
        variant="danger"
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (pendingDeleteId) void copilot.removeConversation(pendingDeleteId);
          setPendingDeleteId(null);
        }}
      />
    </>
  );
}

function ThinkingIndicator({ label }: { label: string }) {
  return (
    <div
      className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground"
      role="status"
      aria-live="polite"
    >
      <AiMark className="h-7 w-7 shrink-0" />
      <span className="agent-chat-thinking font-medium" style={{ color: "var(--ai-accent)" }}>
        {label}
      </span>
    </div>
  );
}

function Composer({
  value,
  pending,
  placeholder,
  sendLabel,
  onChange,
  onSubmit,
}: {
  value: string;
  pending: boolean;
  placeholder: string;
  sendLabel: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}) {
  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit();
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLFormElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      className="mx-auto w-full max-w-3xl px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-6"
      dir={inputDirection(value)}
    >
      <div className="flex items-end gap-2 rounded-2xl border border-border bg-card px-3 py-2 shadow-sm">
        <label htmlFor="admin-copilot-input" className="sr-only">
          {placeholder}
        </label>
        <textarea
          id="admin-copilot-input"
          rows={1}
          value={value}
          disabled={pending}
          placeholder={placeholder}
          onChange={(event) => {
            onChange(event.target.value);
            event.currentTarget.style.height = "auto";
            event.currentTarget.style.height = `${Math.min(event.currentTarget.scrollHeight, 128)}px`;
          }}
          className="max-h-32 min-h-11 min-w-0 flex-1 resize-none bg-transparent py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={pending || !value.trim()}
          aria-label={sendLabel}
          className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[color:var(--ai-on-accent)] disabled:cursor-not-allowed disabled:opacity-40"
          style={{ backgroundColor: "var(--ai-accent)" }}
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      </div>
    </form>
  );
}
